import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle, Users, Calendar, MessageSquare, Shield } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const helpTopics = [
  { value: "starting-invest", label: "Starting to Invest", desc: "I'm a complete beginner and want to start investing" },
  { value: "first-portfolio", label: "Building My First Portfolio", desc: "I want help selecting stocks and creating a diversified portfolio" },
  { value: "risk-tolerance", label: "Understanding My Risk Tolerance", desc: "I need help determining my investment style and risk comfort" },
  { value: "analyzing-stocks", label: "Analyzing Stocks", desc: "I want to learn how to research and evaluate companies" },
  { value: "retirement-planning", label: "Retirement Planning", desc: "I need help planning for long-term retirement goals" },
  { value: "tfsa-rrsp-strategy", label: "TFSA & RRSP Strategy", desc: "I want to optimize my Canadian registered accounts" },
  { value: "debt-investing", label: "Debt Management & Investing", desc: "I have debt and don't know if I should pay it off or invest" },
  { value: "tax-efficient", label: "Tax-Efficient Investing", desc: "I want to minimize taxes on my investments" },
  { value: "portfolio-review", label: "Portfolio Review", desc: "I have investments and want a second opinion on my holdings" },
  { value: "getting-out-debt", label: "Getting Out of Debt", desc: "I need a strategy to eliminate my debt faster" },
  { value: "financial-goals", label: "Setting Financial Goals", desc: "I need help creating a clear financial plan" },
  { value: "other", label: "Other", desc: "My needs are specific or not listed here" },
];

const MentorshipBookingModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    helpTopic: "",
    otherTopic: "",
    preferredDate: "",
    sessionFormat: "",
    specificQuestions: "",
    termsAgreed: false,
    privacyConsent: false,
  });

  // Get minimum date (3 days from now)
  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fullName || formData.fullName.length < 2) {
      toast.error("Please enter your full name (minimum 2 characters)");
      return;
    }
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }
    if (!formData.helpTopic) {
      toast.error("Please select what you need help with");
      return;
    }
    if (formData.helpTopic === "other" && !formData.otherTopic) {
      toast.error("Please specify what you need help with");
      return;
    }
    if (!formData.preferredDate) {
      toast.error("Please select a preferred session date");
      return;
    }
    if (!formData.sessionFormat) {
      toast.error("Please select a session format");
      return;
    }
    if (!formData.termsAgreed) {
      toast.error("Please agree to the terms");
      return;
    }
    if (!formData.privacyConsent) {
      toast.error("Please consent to the privacy policy");
      return;
    }

    setLoading(true);
    try {
      const selectedTopic = helpTopics.find(t => t.value === formData.helpTopic);
      
      // Send to backend which will email compoundrca@gmail.com
      await axios.post(`${API_URL}/api/premium-service/mentorship`, {
        service_type: "One-on-One Mentorship",
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone || "Not provided",
        help_topic: selectedTopic?.label || formData.helpTopic,
        help_topic_desc: formData.helpTopic === "other" ? formData.otherTopic : selectedTopic?.desc,
        preferred_date: formData.preferredDate,
        session_format: formData.sessionFormat,
        specific_questions: formData.specificQuestions || "None provided",
        price: "$0 (Price TBD)",
        submitted_at: new Date().toISOString(),
      });

      setSuccess(true);
    } catch (error) {
      console.error("Failed to send booking:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      helpTopic: "",
      otherTopic: "",
      preferredDate: "",
      sessionFormat: "",
      specificQuestions: "",
      termsAgreed: false,
      privacyConsent: false,
    });
    onOpenChange(false);
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
              Thanks!
            </h3>
            <p className="text-slate-600">
              We&apos;ll send you available time options within 24 hours via email.
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 bg-emerald-500 hover:bg-emerald-600"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl">
                Book Your Mentorship Session
              </DialogTitle>
              <DialogDescription>
                Meet with an experienced investor who will guide you through your journey.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Section 1: Personal Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-navy-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs text-emerald-700 font-bold">1</span>
              Personal Information
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                minLength={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>

          {/* Section 2: Session Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-navy-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs text-emerald-700 font-bold">2</span>
              Session Details
            </h3>
            
            <div className="space-y-2">
              <Label>What do you need help with? *</Label>
              <Select
                value={formData.helpTopic}
                onValueChange={(value) => setFormData({ ...formData, helpTopic: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  {helpTopics.map((topic) => (
                    <SelectItem key={topic.value} value={topic.value}>
                      <div>
                        <span className="font-medium">{topic.label}</span>
                        <span className="text-xs text-slate-500 block">{topic.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.helpTopic === "other" && (
              <div className="space-y-2">
                <Label htmlFor="otherTopic">Please specify *</Label>
                <Textarea
                  id="otherTopic"
                  placeholder="Please describe what you'd like help with..."
                  value={formData.otherTopic}
                  onChange={(e) => setFormData({ ...formData, otherTopic: e.target.value.slice(0, 500) })}
                  maxLength={500}
                  required
                />
                <p className="text-xs text-slate-500">{formData.otherTopic.length}/500 characters</p>
              </div>
            )}
          </div>

          {/* Section 3: Session Preferences */}
          <div className="space-y-4">
            <h3 className="font-semibold text-navy-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs text-emerald-700 font-bold">3</span>
              Session Preferences
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="preferredDate" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Preferred Session Date *
              </Label>
              <Input
                id="preferredDate"
                type="date"
                min={getMinDate()}
                value={formData.preferredDate}
                onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                required
              />
              <p className="text-xs text-slate-500">We&apos;ll confirm the exact time based on availability</p>
            </div>

            <div className="space-y-3">
              <Label>Session Format *</Label>
              <RadioGroup
                value={formData.sessionFormat}
                onValueChange={(value) => setFormData({ ...formData, sessionFormat: value })}
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:border-emerald-300 cursor-pointer">
                  <RadioGroupItem value="video-call" id="video-call" />
                  <Label htmlFor="video-call" className="cursor-pointer flex-1">
                    <span className="font-medium">Video Call (Zoom/Google Meet)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:border-emerald-300 cursor-pointer">
                  <RadioGroupItem value="phone-call" id="phone-call" />
                  <Label htmlFor="phone-call" className="cursor-pointer flex-1">
                    <span className="font-medium">Phone Call</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:border-emerald-300 cursor-pointer">
                  <RadioGroupItem value="in-person" id="in-person" />
                  <Label htmlFor="in-person" className="cursor-pointer flex-1">
                    <span className="font-medium">In-Person (Toronto area only)</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Section 4: Additional Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-navy-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs text-emerald-700 font-bold">4</span>
              Additional Information
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="specificQuestions" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                What specific questions or topics do you want to cover? (Optional but recommended)
              </Label>
              <Textarea
                id="specificQuestions"
                placeholder="Example: I want to understand if I should max out my TFSA or RRSP first, and how to pick my first 3-5 stocks..."
                value={formData.specificQuestions}
                onChange={(e) => setFormData({ ...formData, specificQuestions: e.target.value.slice(0, 1000) })}
                maxLength={1000}
                rows={4}
              />
              <p className="text-xs text-slate-500">{formData.specificQuestions.length}/1000 characters</p>
            </div>
          </div>

          {/* Section 5: Consent & Agreement */}
          <div className="space-y-4">
            <h3 className="font-semibold text-navy-700 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs text-emerald-700 font-bold">5</span>
              Consent & Agreement
            </h3>
            
            <div className="space-y-3 bg-slate-50 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="termsAgreed"
                  checked={formData.termsAgreed}
                  onCheckedChange={(checked) => setFormData({ ...formData, termsAgreed: checked })}
                />
                <Label htmlFor="termsAgreed" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                  I understand this is educational mentorship, not personalized investment advice. I am responsible for my own investment decisions. *
                </Label>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="privacyConsent"
                  checked={formData.privacyConsent}
                  onCheckedChange={(checked) => setFormData({ ...formData, privacyConsent: checked })}
                />
                <Label htmlFor="privacyConsent" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                  I consent to my information being used to schedule my mentorship session and send related communications. *
                </Label>
              </div>
            </div>
          </div>

          {/* Price Notice */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <p className="text-emerald-800 font-medium">Session Price: <span className="text-xl font-bold">$0</span></p>
            <p className="text-emerald-600 text-sm">Price to be determined - We&apos;ll confirm pricing before your session</p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              "Book My Mentorship Session"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MentorshipBookingModal;
